import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Stepper,
	Step,
	StepLabel,
	TextField,
	Checkbox,
	Chip,
	Alert,
	Button,
	ToggleButtonGroup,
	ToggleButton,
	FormControlLabel,
	MenuItem,
	FormControl,
	InputLabel,
	Select,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";

import DatePickerController from "../../components/inputs/DatePickerController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { getCodeDescription, isValidDateTest, maxLengthMessage } from "../../utils/functions";

// ── Validation
const auditSchema = yup.object().shape({
	auditDate: isValidDateTest("Audit Date"),
	auditedBy: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.required("Audited By is required"),
});

const buildDefaults = (userName) => ({
	auditDate: moment().format("MM/DD/YYYY"),
	auditedBy: userName || "",
});

const STEPS = ["Select Items", "Audit Details"];

// ── Component ─
const QuickAuditDialog = ({
	open,
	onClose,
	onSave,
	equipment = [],
	vehicles = [],
	employees = [],
}) => {
	const user = useSelector((state) => state.user);
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	// Step & mode
	const [activeStep, setActiveStep] = useState(0);
	const [mode, setMode] = useState("list");

	// Queue
	const [queue, setQueue] = useState([]);

	// Search & filters
	const [listSearch, setListSearch] = useState("");
	const [filterEmpID, setFilterEmpID] = useState("");
	const [filterVehID, setFilterVehID] = useState("");
	const [filterLoc, setFilterLoc] = useState("");

	// Scan
	const [scanInput, setScanInput] = useState("");
	const [scanMsg, setScanMsg] = useState({ text: "", ok: false });
	const [scannerActive, setScannerActive] = useState(false);
	const videoRef = useRef(null);
	const streamRef = useRef(null);
	const scanInputRef = useRef(null);

	// Confirm close
	const [confirmClose, setConfirmClose] = useState(false);

	// Form (step 2 fields only)
	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(auditSchema),
		defaultValues: buildDefaults(user?.userData?.userName),
	});

	// ── Reset when dialog opens / closes
	useEffect(() => {
		if (open) {
			reset(buildDefaults(user?.userData?.userName));
			setActiveStep(0);
			setMode("list");
			setQueue([]);
			setListSearch("");
			setFilterEmpID("");
			setFilterVehID("");
			setFilterLoc("");
			setScanInput("");
			setScanMsg({ text: "", ok: false });
		} else {
			stopCamera();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => () => stopCamera(), []);

	// ── Memoised helpers
	const uniqueLocations = useMemo(
		() =>
			[
				...new Set(equipment.map((e) => e.location).filter(Boolean)),
			].sort(),
		[equipment],
	);

	const filteredEquip = useMemo(() => {
		const kws = listSearch
			.trim()
			.split(/\s+/)
			.filter(Boolean)
			.map((k) => k.toLowerCase());

		return equipment.filter((eq) => {
			if (
				kws.length &&
				!kws.every(
					(k) =>
						(eq.serialNumber || "").toLowerCase().includes(k) ||
						(eq.assetTag || "").toLowerCase().includes(k) ||
						(eq.propertyNumber || "").toLowerCase().includes(k) ||
						(eq.type || "").toLowerCase().includes(k) ||
						(eq.brand || "").toLowerCase().includes(k) ||
						(eq.model || "").toLowerCase().includes(k),
				)
			)
				return false;

			if (filterEmpID) {
				if (filterEmpID === "0" && eq.perPersonnelId) return false;
				if (
					filterEmpID !== "0" &&
					String(eq.perPersonnelId || "") !== filterEmpID
				)
					return false;
			}
			if (filterVehID) {
				if (filterVehID === "0" && eq.vehicleId) return false;
				if (
					filterVehID !== "0" &&
					String(eq.vehicleId || "") !== filterVehID
				)
					return false;
			}
			if (filterLoc) {
				if (filterLoc === "_none_" && eq.location) return false;
				if (filterLoc !== "_none_" && (eq.location || "") !== filterLoc)
					return false;
			}
			return true;
		});
	}, [equipment, listSearch, filterEmpID, filterVehID, filterLoc]);

	const queueIds = useMemo(() => new Set(queue.map((q) => q.id)), [queue]);

	const allFilteredSelected =
		filteredEquip.length > 0 &&
		filteredEquip.every((eq) => queueIds.has(eq.id));

	// ── Camera
	const stopCamera = () => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		}
		setScannerActive(false);
	};

	const startCamera = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: "environment" },
			});
			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				videoRef.current.play();
			}
			setScannerActive(true);
			setScanMsg({
				text: "Camera active — position barcode in frame",
				ok: true,
			});
		} catch {
			setScanMsg({
				text: "Camera unavailable. Enter barcode manually below.",
				ok: false,
			});
		}
	};

	// ── Queue helpers ───
	const toggleItem = (eq) =>
		setQueue((q) =>
			queueIds.has(eq.id) ? q.filter((x) => x.id !== eq.id) : [...q, eq],
		);

	const removeFromQueue = (id) =>
		setQueue((q) => q.filter((x) => x.id !== id));

	const toggleAll = () => {
		if (allFilteredSelected) {
			setQueue((q) =>
				q.filter((x) => !filteredEquip.some((f) => f.id === x.id)),
			);
		} else {
			const toAdd = filteredEquip.filter((eq) => !queueIds.has(eq.id));
			setQueue((q) => [...q, ...toAdd]);
		}
	};

	// ── Scan handler ────
	const handleScan = (val) => {
		const v = val.trim();
		if (!v) return;

		const found = equipment.find(
			(eq) =>
				eq.serialNumber === v ||
				eq.assetTag === v ||
				eq.propertyNumber === v,
		);

		if (found) {
			if (queueIds.has(found.id)) {
				setScanMsg({
					text: `Already queued: ${found.type} ${found.serialNumber || found.assetTag}`,
					ok: false,
				});
			} else {
				setQueue((q) => [...q, found]);
				setScanMsg({
					text: `✓ Added: ${found.type} — ${[found.brand, found.model].filter(Boolean).join(" ")} (${found.serialNumber || found.assetTag || found.propertyNumber})`,
					ok: true,
				});
			}
		} else {
			setScanMsg({
				text: `No match for "${v}". Try serial #, asset tag, or property #.`,
				ok: false,
			});
		}

		setScanInput("");
		scanInputRef.current?.focus();
	};

	// ── Save ──
	const onSubmit = (data) => {
		if (!queue.length) return;

		onSave(
			queue.map((eq) => eq.id),
			{
				lastInspectionDate: data.auditDate
					? moment(data.auditDate, "MM/DD/YYYY").format("YYYY-MM-DD")
					: null,
				auditedBy: data.auditedBy?.trim() || null,
			},
		);

		reset();
		onClose();
	};

	// ── Close ─
	const handleClose = () => {
		if (queue.length > 0) {
			setConfirmClose(true);
		} else {
			stopCamera();
			onClose();
		}
	};

	const handleConfirmClose = () => {
		setConfirmClose(false);
		stopCamera();
		onClose();
	};

	// Watched values for summary
	const watchedDate = watch("auditDate");
	const watchedBy = watch("auditedBy");

	return (
		<>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
				<DialogTitle>📋 Audit Equipment</DialogTitle>

				<DialogContent>
					{/* ── Stepper ── */}
					<Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
						{STEPS.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>

					{/* ── STEP 1: Select Items ─── */}
					{activeStep === 0 && (
						<Box>
							{/* Mode toggle */}
							<ToggleButtonGroup
								value={mode}
								exclusive
								onChange={(_, v) => {
									if (v) {
										setMode(v);
										if (v === "list") stopCamera();
									}
								}}
								size="small"
								sx={{ mb: 2 }}
							>
								<ToggleButton value="list">
									📋 From List
								</ToggleButton>
								<ToggleButton value="scan">
									📷 Scan Barcodes
								</ToggleButton>
							</ToggleButtonGroup>

							{/* ── List mode ── */}
							{mode === "list" && (
								<Box>
									<TextField
										fullWidth
										size="small"
										label="Search serial #, asset tag, type, brand…"
										value={listSearch}
										onChange={(e) =>
											setListSearch(e.target.value)
										}
										autoFocus
										sx={{ mb: 1 }}
									/>

									{/* Filters */}
									<Box
										sx={{
											display: "flex",
											gap: 1,
											mb: 1.5,
											flexWrap: "wrap",
										}}
									>
										<FormControl
											size="small"
											sx={{ flex: 1, minWidth: 130 }}
										>
											<InputLabel>Employee</InputLabel>
											<Select
												label="Employee"
												value={filterEmpID}
												onChange={(e) =>
													setFilterEmpID(
														e.target.value,
													)
												}
											>
												<MenuItem value="">
													Any Employee
												</MenuItem>
												<MenuItem value="0">
													— Unassigned —
												</MenuItem>
												{employees
													.filter(
														(e) =>
															e.isActive !==
															false,
													)
													.map((e) => (
														<MenuItem
															key={e.value}
															value={String(
																e.value,
															)}
														>
															{e.label}
														</MenuItem>
													))}
											</Select>
										</FormControl>

										<FormControl
											size="small"
											sx={{ flex: 1, minWidth: 130 }}
										>
											<InputLabel>Vehicle</InputLabel>
											<Select
												label="Vehicle"
												value={filterVehID}
												onChange={(e) =>
													setFilterVehID(
														e.target.value,
													)
												}
											>
												<MenuItem value="">
													Any Vehicle
												</MenuItem>
												<MenuItem value="0">
													— Unassigned —
												</MenuItem>
												{vehicles
													.filter(
														(v) =>
															v.status !==
															"Retired",
													)
													.map((v) => (
														<MenuItem
															key={v.value}
															value={String(
																v.value,
															)}
														>
															{v.label}
														</MenuItem>
													))}
											</Select>
										</FormControl>

										<FormControl
											size="small"
											sx={{ flex: 1, minWidth: 120 }}
										>
											<InputLabel>Location</InputLabel>
											<Select
												label="Location"
												value={filterLoc}
												onChange={(e) =>
													setFilterLoc(e.target.value)
												}
											>
												<MenuItem value="">
													Any Location
												</MenuItem>
												<MenuItem value="_none_">
													— No Location —
												</MenuItem>
												{uniqueLocations.map((loc) => (
													<MenuItem
														key={loc}
														value={loc}
													>
														{loc}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										{(filterEmpID ||
											filterVehID ||
											filterLoc) && (
											<Button
												size="small"
												onClick={() => {
													setFilterEmpID("");
													setFilterVehID("");
													setFilterLoc("");
												}}
											>
												✕ Clear filters
											</Button>
										)}
									</Box>

									{/* Select all */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "flex-end",
											mb: 0.5,
										}}
									>
										<FormControlLabel
											control={
												<Checkbox
													checked={
														allFilteredSelected
													}
													onChange={toggleAll}
													size="small"
												/>
											}
											label={
												<Typography variant="caption">
													{allFilteredSelected
														? "Deselect"
														: "Select"}{" "}
													all
													{filteredEquip.length > 0
														? ` (${filteredEquip.length})`
														: ""}
												</Typography>
											}
										/>
									</Box>

									{/* Equipment list */}
									<Box
										sx={{
											maxHeight: 260,
											overflowY: "auto",
											border: "1px solid",
											borderColor: "divider",
											borderRadius: 1,
										}}
									>
										{filteredEquip.length === 0 ? (
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{
													p: 3,
													textAlign: "center",
												}}
											>
												No equipment found
											</Typography>
										) : (
											filteredEquip.map((eq) => {
												const checked = queueIds.has(
													eq.id,
												);
												return (
													<Box
														key={eq.id}
														onClick={() =>
															toggleItem(eq)
														}
														sx={{
															p: "9px 14px",
															cursor: "pointer",
															borderBottom:
																"1px solid",
															borderColor:
																"divider",
															display: "flex",
															alignItems:
																"center",
															gap: 1.5,
															bgcolor: checked
																? "action.selected"
																: "transparent",
															borderLeft:
																"3px solid",
															borderLeftColor:
																checked
																	? "primary.main"
																	: "transparent",
															"&:hover": {
																bgcolor: checked
																	? "action.selected"
																	: "action.hover",
															},
														}}
													>
														<Checkbox
															checked={checked}
															size="small"
															sx={{
																pointerEvents:
																	"none",
																p: 0,
															}}
														/>
														<Box
															sx={{
																flex: 1,
																minWidth: 0,
															}}
														>
															<Typography
																variant="body2"
																fontWeight={600}
															>
																{eq.type} —{" "}
																{[
																	eq.brand,
																	eq.model,
																]
																	.filter(
																		Boolean,
																	)
																	.join(" ")}
															</Typography>
															<Typography
																variant="caption"
																color="text.secondary"
																fontFamily="monospace"
																sx={{
																	display:
																		"block",
																}}
															>
																{
																	eq.serialNumber
																}
																{eq.propertyNumber
																	? ` · ${eq.propertyNumber}`
																	: ""}
																{eq.assetTag
																	? ` · ${eq.assetTag}`
																	: ""}
															</Typography>
															<Typography
																variant="caption"
																color="text.disabled"
																sx={{
																	display:
																		"block",
																}}
															>
																{eq.location ||
																	"No location"}
																{eq.lastInspectionDate
																	? ` · Last audit: ${moment(eq.lastInspectionDate).format("MM/DD/YYYY")}`
																	: " · Never audited"}
															</Typography>
														</Box>
														<Chip
															label={getCodeDescription(
																eq.status,
																dictionary.codePerEquipmentStatus
															) || "-"}
															size="small"
															variant="outlined"
														/>
													</Box>
												);
											})
										)}
									</Box>
								</Box>
							)}

							{/* ── Scan mode ── */}
							{mode === "scan" && (
								<Box>
									<Box
										sx={{
											position: "relative",
											width: "100%",
											aspectRatio: "16/9",
											bgcolor: "#000",
											borderRadius: 1,
											overflow: "hidden",
											mb: 1.5,
										}}
									>
										<video
											ref={videoRef}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
											muted
											playsInline
										/>
										<Box
											sx={{
												position: "absolute",
												inset: 0,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												pointerEvents: "none",
											}}
										>
											<Box
												sx={{
													width: "60%",
													height: "35%",
													border: "2px solid rgba(245,166,35,0.8)",
													borderRadius: 1,
													boxShadow:
														"0 0 0 9999px rgba(0,0,0,0.4)",
												}}
											/>
										</Box>
										{!scannerActive && (
											<Box
												sx={{
													position: "absolute",
													inset: 0,
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													justifyContent: "center",
													gap: 1,
												}}
											>
												<Button
													variant="contained"
													onClick={startCamera}
												>
													📷 Start Camera
												</Button>
												<Typography
													variant="caption"
													sx={{
														color: "rgba(255,255,255,0.6)",
													}}
												>
													or enter barcode manually
													below
												</Typography>
											</Box>
										)}
									</Box>

									{scanMsg.text && (
										<Alert
											severity={
												scanMsg.ok
													? "success"
													: "warning"
											}
											sx={{ mb: 1 }}
										>
											{scanMsg.text}
										</Alert>
									)}

									<Box
										sx={{
											display: "flex",
											gap: 1,
											mb: 0.5,
										}}
									>
										<TextField
											inputRef={scanInputRef}
											fullWidth
											size="small"
											label="Scan or type serial #, asset tag, property # …"
											value={scanInput}
											onChange={(e) => {
												setScanInput(e.target.value);
												setScanMsg({
													text: "",
													ok: false,
												});
											}}
											onKeyDown={(e) =>
												e.key === "Enter" &&
												handleScan(scanInput)
											}
											autoFocus
											InputProps={{
												sx: {
													fontFamily: "monospace",
												},
											}}
										/>
										<Button
											variant="contained"
											onClick={() =>
												handleScan(scanInput)
											}
										>
											Add
										</Button>
									</Box>
									<Typography
										variant="caption"
										color="text.secondary"
									>
										Each scan adds to the queue — scan as
										many as needed before continuing.
									</Typography>
								</Box>
							)}

							{/* ── Queue preview ── */}
							{queue.length > 0 && (
								<Box
									sx={{
										mt: 2,
										p: 2,
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
										>
											🗂 Queue — {queue.length} item
											{queue.length !== 1 ? "s" : ""}{" "}
											selected
										</Typography>
										<Button
											size="small"
											onClick={() => setQueue([])}
										>
											Clear all
										</Button>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexWrap: "wrap",
											gap: 0.75,
										}}
									>
										{queue.map((eq) => (
											<Chip
												key={eq.id}
												label={`${eq.type} ${eq.serialNumber || eq.assetTag || eq.propertyNumber || `#${eq.id}`}`}
												size="small"
												onDelete={() =>
													removeFromQueue(eq.id)
												}
											/>
										))}
									</Box>
								</Box>
							)}
						</Box>
					)}

					{/* ── STEP 2: Audit Details ── */}
					{activeStep === 1 && (
						<Box>
							{/* Queue summary with removal */}
							<Box
								sx={{
									p: 2,
									border: "1px solid",
									borderColor: "primary.main",
									borderRadius: 1,
									mb: 3,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										mb: 1,
										display: "block",
									}}
								>
									Auditing {queue.length} item
									{queue.length !== 1 ? "s" : ""} — click ✕ to
									remove before completing
								</Typography>
								<Box
									sx={{
										display: "flex",
										flexWrap: "wrap",
										gap: 0.75,
									}}
								>
									{queue.map((eq) => (
										<Chip
											key={eq.id}
											label={`${eq.type} ${eq.serialNumber || eq.assetTag || eq.propertyNumber || `#${eq.id}`}`}
											size="small"
											onDelete={() =>
												removeFromQueue(eq.id)
											}
										/>
									))}
								</Box>
							</Box>

							{/* Form fields */}
							<Box
								component="form"
								onSubmit={handleSubmit(onSubmit)}
								noValidate
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<DatePickerController
										name="auditDate"
										label="Audit Date"
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="auditedBy"
										label="Audited By"
										control={control}
										errors={errors}
									/>
								</Box>
							</Box>

							{/* Summary */}
							<Box
								sx={{
									mt: 2,
									p: 2,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										mb: 1,
										display: "block",
									}}
								>
									Summary
								</Typography>
								<Box
									sx={{
										display: "flex",
										gap: 2,
										flexWrap: "wrap",
									}}
								>
									<Typography variant="body2">
										📋 {queue.length} item
										{queue.length !== 1 ? "s" : ""} will be
										marked audited
									</Typography>
									<Typography variant="body2">
										📅 {watchedDate || "—"}
									</Typography>
									<Typography variant="body2">
										👤 {watchedBy || "—"}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 2 }}>
					{activeStep === 1 && (
						<Button onClick={() => setActiveStep(0)}>← Back</Button>
					)}
					<Box sx={{ flex: 1 }} />
					{activeStep === 0 && (
						<Button
							variant="contained"
							disabled={queue.length === 0}
							onClick={() => setActiveStep(1)}
						>
							Next → ({queue.length} item
							{queue.length !== 1 ? "s" : ""})
						</Button>
					)}
					{activeStep === 1 && (
						<PATSaveButton
							onClick={handleSubmit(onSubmit)}
							tooltipText={`Complete Audit (${queue.length})`}
						/>
					)}
					<PATCloseButton onClick={handleClose} tooltipText="Close" />
				</DialogActions>
			</Dialog>

			{/* ── Confirm‑close dialog ── */}
			<Dialog
				open={confirmClose}
				onClose={() => setConfirmClose(false)}
				maxWidth="xs"
			>
				<DialogTitle>⚠️ Discard selection?</DialogTitle>
				<DialogContent>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ pt: 1 }}
					>
						You have {queue.length} item
						{queue.length !== 1 ? "s" : ""} selected. Closing will
						discard this audit queue.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmClose(false)}>
						Keep editing
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleConfirmClose}
					>
						Discard &amp; close
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default QuickAuditDialog;
